import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Picaluna website publishing (Accounts page card). Thin wrapper over the
 * /api/picaluna-site endpoints — the pipeline itself (snapshot sync, staging
 * deploy, promote-by-PR-merge) runs in GitHub Actions on the picaluna-v3 repo;
 * see Conductor/picaluna-v3-integration-SITE-PUBLISHING.md.
 */

/** event_type of the dispatch that auto-publishes a ceremony to production. */
export const CONDUCTOR_CHANGED_EVENT = 'conductor-changed';

/**
 * event_type of a selective promote — a subset of the staged changes synced
 * straight onto main + deployed to production inline, with the remainder
 * re-staged on the preview PR. A run with this title is a PRODUCTION publish.
 */
export const SCOPED_PROMOTE_EVENT = 'picaluna-promote-scoped';

export interface PicalunaWorkflowRun {
  /** GitHub's run id — what cancel() addresses. */
  id: number;
  status: 'queued' | 'in_progress' | 'completed' | string;
  conclusion: 'success' | 'failure' | 'cancelled' | string | null;
  event: string;
  /**
   * GitHub's display_title. For a repository_dispatch this is the event_type,
   * which is the ONLY way to tell a ceremony auto-publish from a staging
   * preview — `event` is "repository_dispatch" for both.
   */
  displayTitle: string;
  htmlUrl: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * True when this run of the SYNC workflow is a ceremony auto-publish, i.e. it
 * builds and deploys PRODUCTION inline rather than a staging preview.
 *
 * sync-conductor.yml gained an inline production deploy on the auto-prod path
 * (a push made with GITHUB_TOKEN cannot trigger deploy.yml — GitHub's
 * infinite-loop guard). Since then a conductor-changed run of the sync workflow
 * IS the production publish, and deploy.yml no longer fires for it. Rendering it
 * under "Staging preview" is wrong on both counts.
 */
export function isProductionPublishRun(run: PicalunaWorkflowRun | null | undefined): boolean {
  return (
    !!run &&
    (run.displayTitle === CONDUCTOR_CHANGED_EVENT || run.displayTitle === SCOPED_PROMOTE_EVENT)
  );
}

export interface PicalunaPendingChanges {
  number: number;
  title: string;
  htmlUrl: string;
  updatedAt: string;
}

export interface PicalunaStagedItem {
  /** Scope type ('blogs', 'regions', …). */
  type: string;
  /** Human heading for the type ("Blog posts"). */
  typeLabel: string;
  id: string;
  label: string;
  kind: 'added' | 'changed' | 'removed';
  detail?: string;
  /** Publish-scope token, or null when the item can't ship on its own. */
  token: string | null;
}

export interface PicalunaStagedChanges {
  prNumber: number;
  prTitle: string;
  prUrl: string;
  items: PicalunaStagedItem[];
}

export interface PicalunaSiteStatus {
  stagingUrl: string;
  productionUrl: string;
  sync: PicalunaWorkflowRun | null;
  deploy: PicalunaWorkflowRun | null;
  pendingChanges: PicalunaPendingChanges | null;
}

@Injectable({
  providedIn: 'root',
})
export class PicalunaSiteService {
  constructor(private http: HttpClient) {}

  /**
   * Kick off a staging publish.
   *
   * `scope` narrows it to specific content — "blogs:what-is-a-vigil" ships that
   * post and leaves everything else exactly as deployed. Omit for a full sync of
   * everything currently published, which is what the Website page's top card
   * does. Format is "<type>[:<slug>]", space-separated for several; the server
   * validates and rejects anything else.
   */
  publish(scope?: string): Observable<{ dispatched: boolean }> {
    const url = environment.api.host + environment.api.paths.api.picalunaSite.publish;

    return this.http
      .post<{ data: { dispatched: boolean } }>(url, scope ? { scope } : {})
      .pipe(map((res) => res.data));
  }

  status(): Observable<PicalunaSiteStatus> {
    const url = environment.api.host + environment.api.paths.api.picalunaSite.status;

    return this.http.get<{ data: PicalunaSiteStatus }>(url).pipe(map((res) => res.data));
  }

  /**
   * Promote to production. No `scope` (or one covering everything staged)
   * merges the preview PR. A partial scope — space-separated tokens from the
   * staged-changes list — ships just those items and leaves the rest staged.
   */
  promote(
    scope?: string
  ): Observable<{ prNumber: number; scoped?: boolean; promoted?: number; remaining?: number }> {
    const url = environment.api.host + environment.api.paths.api.picalunaSite.promote;

    return this.http
      .post<{ data: { prNumber: number; scoped?: boolean; promoted?: number; remaining?: number } }>(
        url,
        scope ? { scope } : {}
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Item-level list of what the preview PR stages relative to the live site —
   * feeds the selective-promote dialog. `token` is null for an item that can
   * only ship as part of a full promote.
   */
  stagedChanges(): Observable<PicalunaStagedChanges> {
    const url = environment.api.host + environment.api.paths.api.picalunaSite.stagedChanges;

    return this.http.get<{ data: PicalunaStagedChanges }>(url).pipe(map((res) => res.data));
  }

  /**
   * Ask GitHub to stop an in-flight run.
   *
   * Accepted (202) means "cancellation requested", not "stopped" — the run
   * keeps reporting in_progress for a few seconds before settling on
   * conclusion: cancelled, so keep polling rather than assuming it ended.
   */
  cancel(runId: number): Observable<{ cancelled: boolean }> {
    const url = environment.api.host + environment.api.paths.api.picalunaSite.cancel;

    return this.http
      .post<{ data: { cancelled: boolean } }>(url, { runId })
      .pipe(map((res) => res.data));
  }

  /**
   * Poll the pipeline after a publish() dispatch and emit ONCE with the sync
   * workflow run when a run created after `sinceEpochMs` (small clock-skew
   * allowance — older runs are unrelated) reaches completion. Completes without
   * emitting if the cap (~12 min) passes first. Status errors are swallowed
   * (transient — keep polling). Shared by the per-entity "Preview on staging"
   * buttons (RSVP panel, profile page).
   */
  pollSyncCompletion(
    sinceEpochMs: number,
    tickMs: number = 15_000,
    maxTicks: number = 48
  ): Observable<PicalunaWorkflowRun> {
    return timer(20_000, tickMs).pipe(
      take(maxTicks),
      switchMap(() => this.status().pipe(catchError(() => of(null)))),
      map((status) => status?.sync ?? null),
      filter(
        (run): run is PicalunaWorkflowRun =>
          !!run &&
          run.status === 'completed' &&
          // A PRODUCTION publish (a ceremony auto-publish, or someone else's
          // selective promote) is not the staging preview this caller asked
          // for. Without this an arranger waiting on their ceremony preview
          // got "Staging preview ready" from a content manager's promote —
          // a statement about a build that never touched their page.
          !isProductionPublishRun(run) &&
          new Date(run.createdAt).getTime() >= sinceEpochMs - 120_000
      ),
      take(1)
    );
  }
}
